/**
 * AdminMemories.jsx — Cross-user Fissal memory admin widget (#250).
 *
 * Modernized with ThemedTable:
 *   - Sortable User, Discord ID, Fact count, and Last Seen columns
 *   - Draggable reordering and real-time search filter
 *   - Expandable fact inspector per user
 */
import { Show, For, createSignal, createMemo } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import { EMOJI } from '../lib/emoji.js';
import toast from '../lib/toastStore.js';
import ConfirmDialog from '../components/ConfirmDialog.jsx';
import ThemedTable, { RelTimeCell } from '../components/ThemedTable/index.js';

async function apiFetch(url, opts = {}) {
  const r = await fetch(url, { credentials: 'same-origin', ...opts });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${r.status}`);
  }
  return r.json();
}

export function AdminMemoriesPanel(props = {}) {
  const [params] = createSignal({});
  const { data, loading, error, refetch } = useWidgetData('admin-memories', params);

  const [busy,         setBusy]         = createSignal(false);
  const [expandedUser, setExpandedUser] = createSignal(null);
  const [userFacts,    setUserFacts]    = createSignal(null);
  const [factsLoading, setFactsLoading] = createSignal(false);
  const [confirmState, setConfirmState] = createSignal(null);

  const users = createMemo(() => data()?.users || []);

  async function loadUserFacts(userId) {
    if (expandedUser() === userId) {
      setExpandedUser(null);
      setUserFacts(null);
      return;
    }
    setExpandedUser(userId);
    setUserFacts(null);
    setFactsLoading(true);
    try {
      const d = await apiFetch(`/api/admin/memories/${encodeURIComponent(userId)}`);
      setUserFacts(d);
    } catch (e) {
      toast.error('Failed to load facts', { detail: e.message });
      setExpandedUser(null);
    } finally {
      setFactsLoading(false);
    }
  }

  function deleteFact(userId, fact) {
    if (busy()) return;
    const preview = (fact.fact || '').slice(0, 80);
    setConfirmState({
      title: 'Delete this fact?',
      body: <>Permanently removes <code>{preview}{fact.fact && fact.fact.length > 80 ? '…' : ''}</code> from this user's memory. Cannot be undone.</>,
      confirmLabel: 'Delete',
      confirmKind: 'danger',
      action: async () => {
        setBusy(true);
        try {
          await apiFetch(`/api/admin/memories/${encodeURIComponent(userId)}/${fact.id}`, { method: 'DELETE' });
          toast.success('Fact deleted.');
          const d = await apiFetch(`/api/admin/memories/${encodeURIComponent(userId)}`);
          setUserFacts(d);
          await refetch();
        } catch (e) {
          toast.error('Delete failed', { detail: e.message });
        } finally {
          setBusy(false);
        }
      },
    });
  }

  function wipeUser(userId, displayName) {
    if (busy()) return;
    setConfirmState({
      title: `Wipe ALL memories for ${displayName}?`,
      body: 'This permanently deletes every fact Fissal has stored about this user. Cannot be undone.',
      confirmLabel: 'Wipe',
      confirmKind: 'danger',
      action: async () => {
        setBusy(true);
        try {
          await apiFetch(`/api/admin/memories/${encodeURIComponent(userId)}`, { method: 'DELETE' });
          toast.success('User memories wiped.');
          if (expandedUser() === userId) {
            setExpandedUser(null);
            setUserFacts(null);
          }
          await refetch();
        } catch (e) {
          toast.error('Wipe failed', { detail: e.message });
        } finally {
          setBusy(false);
        }
      },
    });
  }

  const columns = [
    {
      key: 'displayName',
      label: 'User',
      sortable: true,
      type: 'string',
      canHide: false,
      render: (_v, u) => (
        <button
          type="button"
          class="v2-admin-memories__name-btn"
          style={{ background: 'none', border: 'none', color: 'var(--cyan, #38bdf8)', 'font-weight': '600', cursor: 'pointer', 'text-align': 'left' }}
          onClick={() => loadUserFacts(u.discordUserId)}
          title="View / hide facts for this user"
        >
          👤 {u.displayName}
        </button>
      ),
    },
    {
      key: 'discordUserId',
      label: 'Discord ID',
      sortable: true,
      type: 'string',
      render: (v) => <span class="v2-tt-mono v2-tt-dim" style={{ 'font-size': '11px' }}>{v}</span>,
    },
    {
      key: 'factCount',
      label: 'Facts',
      sortable: true,
      type: 'number',
      align: 'right',
      getValue: u => u.factCount || 0,
      render: (v) => <span class="v2-tt-mono" style={{ color: 'var(--gold, #ffc857)', 'font-weight': '600' }}>{v}</span>,
    },
    {
      key: 'lastSeen',
      label: 'Last seen',
      sortable: true,
      type: 'time',
      align: 'right',
      getValue: u => u.lastSeen || 0,
      render: (v) => <RelTimeCell ts={v} />,
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'right',
      render: (_v, u) => (
        <div style={{ display: 'flex', gap: '4px', 'justify-content': 'flex-end' }}>
          <button
            type="button"
            class="v2-tt-btn"
            style={{ padding: '2px 7px', 'font-size': '11px' }}
            disabled={busy()}
            onClick={() => loadUserFacts(u.discordUserId)}
          >
            {expandedUser() === u.discordUserId ? 'Hide' : 'View'}
          </button>
          <button
            type="button"
            class="btn btn-xs btn-danger"
            style={{ padding: '2px 7px', 'font-size': '11px' }}
            disabled={busy() || u.factCount === 0}
            onClick={() => wipeUser(u.discordUserId, u.displayName)}
          >
            Wipe
          </button>
        </div>
      ),
    },
  ];

  return (
    <div class={`v2-admin-memories ${props.embedded ? 'v2-admin-memories--embedded' : ''}`}>
      <Show when={loading()}>
        <div class="v2-loading">{EMOJI.loading} Loading memories…</div>
      </Show>
      <Show when={error()}>
        <div class="v2-error">{EMOJI.warn} {error()}</div>
      </Show>

      <Show when={!loading() && data()}>
        <ThemedTable
          columns={columns}
          rows={users()}
          storageKey="admin_memories"
          defaultSortKey="factCount"
          defaultSortDir="desc"
          searchPlaceholder="Search user name or Discord ID…"
          emptyText="No AI user memories stored."
          extraControls={
            <button
              type="button"
              class="v2-tt-btn"
              disabled={loading() || busy()}
              onClick={() => refetch()}
              title="Refresh list"
            >↻ Refresh</button>
          }
        />

        {/* Fact Drawer for Expanded User */}
        <Show when={expandedUser()}>
          <div class="v2-admin-memories__drawer" style={{ 'margin-top': '10px', padding: '10px', background: 'rgba(0,0,0,0.3)', 'border-radius': '8px', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div class="v2-admin-memories__drawer-hdr" style={{ display: 'flex', 'align-items': 'center', 'margin-bottom': '8px' }}>
              <span style={{ 'font-weight': '600', color: 'var(--t-bright, #fff)' }}>
                Facts for <strong style={{ color: 'var(--cyan, #38bdf8)' }}>{userFacts()?.displayName || expandedUser()}</strong>
              </span>
              <button
                type="button"
                class="v2-widget-hdr__btn"
                style={{ 'margin-left': 'auto' }}
                onClick={() => setExpandedUser(null)}
              >✕</button>
            </div>

            <Show when={factsLoading()}>
              <div class="v2-loading">Loading facts…</div>
            </Show>

            <Show when={!factsLoading() && userFacts()}>
              <Show when={(userFacts().facts || []).length === 0}>
                <div class="v2-empty">No facts stored for this user.</div>
              </Show>
              <Show when={(userFacts().facts || []).length > 0}>
                <div class="v2-admin-memories__fact-list" style={{ display: 'flex', 'flex-direction': 'column', gap: '6px' }}>
                  <For each={userFacts().facts}>{(f) => (
                    <div class="v2-admin-memories__fact" style={{
                      display: 'flex', 'align-items': 'center', gap: '8px', padding: '6px 10px',
                      background: 'rgba(255,255,255,0.03)', 'border-radius': '6px', border: '1px solid rgba(255,255,255,0.05)'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ 'font-size': '12px', color: 'var(--t-bright, #fff)' }}>{f.fact}</div>
                        <Show when={f.category}>
                          <span class="v2-tt-meta-badge" style={{ 'font-size': '10px', 'margin-top': '3px', display: 'inline-block' }}>{f.category}</span>
                        </Show>
                      </div>
                      <button
                        type="button"
                        class="btn btn-xs btn-danger"
                        disabled={busy()}
                        onClick={() => deleteFact(expandedUser(), f)}
                        title="Delete this fact"
                      >Delete</button>
                    </div>
                  )}</For>
                </div>
              </Show>
            </Show>
          </div>
        </Show>
      </Show>

      {/* Confirmation dialog */}
      <Show when={confirmState()}>
        <ConfirmDialog
          title={confirmState().title}
          body={confirmState().body}
          confirmLabel={confirmState().confirmLabel}
          confirmKind={confirmState().confirmKind}
          onConfirm={confirmState().action}
          onCancel={() => setConfirmState(null)}
        />
      </Show>
    </div>
  );
}

export default function AdminMemories(props) {
  return (
    <div class="v2-widget">
      <div class="v2-widget-hdr">
        <span class="v2-widget-hdr__title">🧠 AI Memory Governance</span>
      </div>
      <div class="v2-widget__scroll" style={{ padding: '0.5rem' }}>
        <AdminMemoriesPanel {...props} />
      </div>
    </div>
  );
}
